import { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  AsyncSearchSelect,
  type AsyncSearchSelectOption,
} from '@ideon-api/ideon-ui'
import { useAccountOptionSearch } from '../../lib/use-account-search'
import { useBindIdentifier } from '../../lib/use-bind-identifier'

interface BindAccountDialogProps {
  isOpen: boolean
  onClose: () => void
  threescaleAppId: string
  onBound: (result: { bound: boolean }) => void
}

export function BindAccountDialog({ isOpen, onClose, threescaleAppId, onBound }: BindAccountDialogProps) {
  const { bindIdentifier } = useBindIdentifier()
  const searchAccounts = useAccountOptionSearch()
  const [selectedAccount, setSelectedAccount] = useState<AsyncSearchSelectOption | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ bound: boolean } | null>(null)
  // Guards against a rapid double-click firing two bind calls -- a ref is
  // checked synchronously at the top of the handler, so it can't miss the
  // window between the two clicks the way a `disabled={submitting}` prop
  // (which only takes effect after React re-renders) or a handler closing
  // over a stale `submitting` value could.
  const submittingRef = useRef(false)

  // Reused across rows (one dialog instance, opened per identifier), so
  // reopening for a different -- or the same -- row must start clean.
  useEffect(() => {
    if (isOpen) {
      setSelectedAccount(null)
      setError(null)
      setResult(null)
    }
  }, [isOpen, threescaleAppId])

  const handleOpenChange = (open: boolean) => {
    if (open || submitting) return
    onClose()
  }

  const handleConfirm = async () => {
    if (submittingRef.current || !selectedAccount) return
    submittingRef.current = true
    setSubmitting(true)
    setError(null)
    try {
      const bindResult = await bindIdentifier(selectedAccount.value, threescaleAppId)
      setResult(bindResult)
      onBound(bindResult)
    } catch (bindError) {
      // Keep the selected account -- a 409/403 shouldn't force the admin to
      // re-search for the account they already picked.
      setError(bindError instanceof Error ? bindError.message : 'Failed to bind identifier.')
    } finally {
      submittingRef.current = false
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bind SFDC account</DialogTitle>
        </DialogHeader>

        {result ? (
          <div data-testid="bind-success" data-bound={String(result.bound)}>
            <Alert variant="success">
              {result.bound
                ? `Bound threescale app "${threescaleAppId}" to ${selectedAccount?.label ?? 'the selected account'}.`
                : `Already bound to ${selectedAccount?.label ?? 'this account'} -- no changes were needed.`}
            </Alert>
          </div>
        ) : (
          <>
            <p className="text-sm text-ideon-gray-70">
              Search for the account that owns threescale app{' '}
              <span className="font-semibold text-ideon-gray-black">{threescaleAppId}</span>.
            </p>

            <AsyncSearchSelect
              value={selectedAccount}
              onChange={setSelectedAccount}
              onSearch={searchAccounts}
              placeholder="Search by name or slug"
              aria-label="Account"
            />

            {error && <Alert variant="error">{error}</Alert>}
          </>
        )}

        <DialogFooter>
          {result ? (
            <Button variant="filled" onClick={onClose}>Close</Button>
          ) : (
            <>
              <Button variant="text" onClick={() => handleOpenChange(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button variant="filled" onClick={handleConfirm} disabled={!selectedAccount || submitting}>
                Confirm
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
